"use client";

import { c as _c } from "react/compiler-runtime";
import { Download, FolderArchive, RotateCcw } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { AdLayout } from "@/components/AdLayout";
import { DropZone } from "@/components/DropZone";
import { FileStrip } from "@/components/FileStrip";
import { SiteFooter } from "@/components/SiteFooter";
import { ServerNotice } from "@/components/ServerNotice";
import { SiteHeader } from "@/components/SiteHeader";
import { StepIndicator, type Step } from "@/components/StepIndicator";
import { ToolCard } from "@/components/ToolCard";
import { useToolExtras } from "@/components/ToolExtras";
import { formatBytes } from "@/lib/files";
import { zipFiles } from "@/lib/zip";
import { handOffFiles } from "@/lib/handoff";
import { toolsForFiles, type Tool } from "@/lib/tools";
import { jsonLdText, toolJsonLd } from "@/lib/seo";
export type ToolResult = {
  blob: Blob;
  /** Already in `<original>-<action>.<ext>` form (see outputName in lib/files). */
  name: string;
  /** Extra fact shown under the name, e.g. "1200 × 900 px". */
  detail?: string;
};
type Props = {
  tool: Tool;
  /** The original files. The page keeps them and never changes them. */
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Plain-language messages to show above the workspace (see useToolFiles). */
  notices?: string[];
  /** Set when the job is done; moves the shell to the Download step. */
  results?: ToolResult[] | null;
  /** Clear files and results to begin again (FR-6). */
  onStartOver: () => void;
  /** Name for the batch ZIP, e.g. "images-resized.zip". */
  zipName?: string;
  /** Shown above the download buttons, e.g. a before/after slider. */
  resultPreview?: React.ReactNode;
  /** Replaces the plain file list in the Adjust step (e.g. Merge's sortable list). */
  fileList?: React.ReactNode;
  /** Passed to the file list in the Adjust step. */
  fileDetails?: (string | undefined)[];
  selectedFile?: number;
  onSelectFile?: (index: number) => void;
  /** The tool's own workspace for the Adjust step. */
  children: React.ReactNode;
};

/** The frame every tool page uses (hard rule 3): Add files → Adjust → Download. */
export function ToolShell({
  tool,
  files,
  onFilesChange,
  results,
  onStartOver,
  notices = [],
  zipName = `${tool.slug}.zip`,
  resultPreview,
  fileDetails,
  selectedFile,
  onSelectFile,
  fileList,
  children
}: Props) {
  const step: Step = files.length === 0 ? 1 : results?.length ? 3 : 2;
  // Questions and related tools from the page's ToolPage wrapper.
  const extras = useToolExtras();
  const categoryLabel = tool.category === "pdf" ? "PDF tools" : "Image tools";
  return <>
      <SiteHeader variant="tool" server={tool.runsOn === "server"} />
      <AdLayout>
        <main className="flex flex-col gap-5 py-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground">
                <a href={`/#${tool.category}-tools`} className="underline underline-offset-4 hover:text-ink">
                  {categoryLabel}
                </a>{" "}
                / {tool.name}
              </p>
              <h1 className="mt-0.5 text-[28px] leading-tight sm:text-[34px]">
                {tool.name}
              </h1>
            </div>
            <StepIndicator current={step} />
          </div>

          {notices.length > 0 && <ul className="text-sm font-medium text-destructive">
              {notices.map(m => <li key={m}>{m}</li>)}
            </ul>}

          {tool.runsOn === "server" && step < 3 && <ServerNotice />}

          {step === 1 && <section aria-label="Add files" className="rounded-[20px] bg-card p-3 shadow-[0_1px_0_#e6e9f2] sm:p-4">
              <DropZone accepts={tool.accepts} maxFiles={tool.maxFiles} maxBytes={tool.maxBytes} acceptsLabel={tool.acceptsLabel} onFiles={onFilesChange} subtitle={tool.description} />
            </section>}

          {step === 2 && <div className="flex flex-col gap-4">
              {fileList ?? <FileStrip files={files} details={fileDetails} selected={selectedFile} onSelect={files.length > 1 ? onSelectFile : undefined} onRemove={i => onFilesChange(files.filter((_, j) => j !== i))} />}
              {tool.maxFiles > 1 && files.length < tool.maxFiles && <DropZone variant="compact" accepts={tool.accepts} maxFiles={tool.maxFiles} maxBytes={tool.maxBytes} acceptsLabel={tool.acceptsLabel} existing={files.length} onFiles={more => onFilesChange([...files, ...more])} />}
              {children}
            </div>}

          {step === 3 && results && <DownloadPanel tool={tool} results={results} zipName={zipName} preview={resultPreview} onStartOver={onStartOver} />}
        </main>
        <HowTo tool={tool} />
        {extras}
      </AdLayout>
      <SiteFooter />
    </>;
}
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  // Give the browser a moment to start the download before freeing memory.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Short how-to for people and search engines (NFR-10). Not in the workspace. */
function HowTo(t0) {
  const $ = _c(17);
  const {
    tool
  } = t0;
  let t1;
  if ($[0] !== tool.name) {
    t1 = <h2 id="how-to-heading" className="text-2xl">How to use {tool.name}</h2>;
    $[0] = tool.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tool.howTo) {
    t2 = tool.howTo.map(_temp);
    $[2] = tool.howTo;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <ol className="flex list-decimal flex-col gap-2 pl-6 text-[17px] text-ink">{t2}</ol>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== tool.seoDescription) {
    t4 = <p className="text-muted-foreground">{tool.seoDescription}</p>;
    $[6] = tool.seoDescription;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== tool) {
    t5 = jsonLdText(toolJsonLd(tool));
    $[8] = tool;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: t5
    }} />;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t4 || $[15] !== t6) {
    t7 = <section aria-labelledby="how-to-heading" className="mt-6 mb-12 flex max-w-[720px] flex-col gap-3 border-t pt-8">{t1}{t3}{t4}{t6}</section>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp(step) {
  return <li key={step}>{step}</li>;
}
const downloadButton = "inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60";
function DownloadPanel({
  tool,
  results,
  zipName,
  preview,
  onStartOver
}: {
  tool: Tool;
  results: ToolResult[];
  zipName: string;
  preview?: React.ReactNode;
  onStartOver: () => void;
}) {
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState("");
  const batch = results.length > 1;
  async function downloadZip() {
    setZipping(true);
    setZipError("");
    try {
      saveBlob(await zipFiles(results), zipName);
    } catch {
      setZipError("The ZIP couldn't be made. Try downloading the files one by one.");
    } finally {
      setZipping(false);
    }
  }

  // "Use this file in another tool" works on a single result (FR-6).
  const asFiles = results.length === 1 ? [new File([results[0].blob], results[0].name, {
    type: results[0].blob.type
  })] : [];
  const nextTools = toolsForFiles(asFiles).filter(t => t.slug !== tool.slug);
  return <section aria-label="Download" className="flex flex-col gap-5 rounded-[16px] border bg-card p-5 sm:p-6">
      <h2 className="text-2xl">
        {batch ? `Your ${results.length} files are ready` : "Your file is ready"}
      </h2>
      {preview}
      {batch && <div className="flex flex-col gap-2">
          <button type="button" onClick={downloadZip} disabled={zipping} className={`${downloadButton} self-start`}>
            <FolderArchive className="size-5" aria-hidden="true" />
            {zipping ? "Making ZIP…" : `Download all (${zipName})`}
          </button>
          {zipError && <p className="text-sm font-medium text-destructive">{zipError}</p>}
        </div>}
      <FileList batch={batch}>
        {results.map(r => <li key={r.name} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-canvas p-3">
            <span className="min-w-0">
              <span className="block truncate font-semibold">{r.name}</span>
              <span className="text-sm text-muted-foreground">
                {[r.detail, formatBytes(r.blob.size)].filter(Boolean).join(" · ")}
              </span>
            </span>
            <button type="button" onClick={() => saveBlob(r.blob, r.name)} className={batch ? "inline-flex min-h-11 items-center gap-2 rounded-lg border-[1.5px] border-ink px-4 font-semibold hover:bg-sand" : downloadButton}>
              <Download className="size-5" aria-hidden="true" />
              Download
              {batch && <span className="sr-only"> {r.name}</span>}
            </button>
          </li>)}
      </FileList>
      <p className="text-sm text-muted-foreground">
        {tool.runsOn === "server" ? "Made by our converter, which has already deleted your file." : "Made on your device. Nothing was uploaded."}
      </p>
      <button type="button" onClick={onStartOver} className="inline-flex min-h-11 items-center gap-2 self-start rounded-lg border-[1.5px] border-ink px-4 font-semibold hover:bg-sand">
        <RotateCcw className="size-4" aria-hidden="true" />
        Start over
      </button>
      {nextTools.length > 0 && <div className="flex flex-col gap-3 border-t pt-5">
          <h3 className="text-lg">Use this file in another tool</h3>
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-3">
            {nextTools.map(t_0 => <li key={t_0.slug} className="grid">
                <ToolCard tool={t_0} onClick={() => handOffFiles(asFiles)} />
              </li>)}
          </ul>
        </div>}
      <Link href="/" className="inline-flex min-h-11 items-center self-start font-semibold underline underline-offset-4">
        All tools
      </Link>
    </section>;
}

/** One result: plain list. A batch: the list folds away under the ZIP button. */
function FileList(t0) {
  const $ = _c(5);
  const {
    batch,
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ul className="flex flex-col gap-2">{children}</ul>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const list = t1;
  if (!batch) {
    return list;
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <summary className="inline-flex min-h-11 cursor-pointer items-center font-semibold underline underline-offset-4">Download one by one</summary>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== list) {
    t3 = <details className="group">{t2}<div className="mt-2 max-h-[420px] overflow-y-auto">{list}</div></details>;
    $[3] = list;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}